import { preselectService } from "@/lib/scroll";
import Icon from "@/components/Icon";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const NODES = [
    { cx: 110, cy: 34, icon: "home" },
    { cx: 34, cy: 96, icon: "building" },
    { cx: 186, cy: 96, icon: "store" },
    { cx: 74, cy: 128, icon: "video" },
    { cx: 150, cy: 130, icon: "zap" },
];

function NetworkGraph() {
    const center = { cx: 110, cy: 88 };
    return (
        <svg viewBox="0 0 220 160" className="w-full" role="img" aria-label="Broadband network connecting homes and businesses">
            {NODES.map((n, i) => (
                <line
                    key={i}
                    x1={center.cx}
                    y1={center.cy}
                    x2={n.cx}
                    y2={n.cy}
                    stroke="#00CFFF"
                    strokeWidth="1.2"
                    opacity="0.55"
                    className="dash-flow"
                    style={{ animationDelay: `${i * 0.3}s` }}
                />
            ))}
            <circle cx={center.cx} cy={center.cy} r="16" fill="#0B1B33" stroke="#00CFFF" strokeWidth="1.6" />
            <circle cx={center.cx} cy={center.cy} r="16" fill="none" stroke="#00CFFF" strokeWidth="1" className="animate-node-ping" />
            <path d="M104 88 a8.5 8.5 0 0 1 12 0 M106.5 92 a5 5 0 0 1 7 0" fill="none" stroke="#00CFFF" strokeWidth="1.6" />
            <circle cx={center.cx} cy={center.cy + 3.5} r="1.8" fill="#00CFFF" />
            {NODES.map((n, i) => (
                <g key={i}>
                    <circle cx={n.cx} cy={n.cy} r="9" fill="#0B1B33" stroke="rgba(0,207,255,0.6)" strokeWidth="1.2" />
                    <circle cx={n.cx} cy={n.cy} r="2.4" fill={i % 2 ? "#126EFF" : "#00CFFF"} className="animate-pulse-dot" style={{ animationDelay: `${i * 0.45}s` }} />
                </g>
            ))}
        </svg>
    );
}

export default function Broadband() {
    const { t } = useLang();
    return (
        <section id="broadband" data-testid="broadband-section" className="relative py-24 md:py-32 lg:py-40 bg-surface overflow-hidden">
            <div className="absolute -bottom-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-electric/10 blur-[140px] pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal delay={0.15} className="order-2 lg:order-1">
                    <div className="relative rounded-3xl border border-electric/25 bg-white/75 p-8 overflow-hidden shadow-[0_20px_60px_rgba(13,27,46,0.08)]">
                        <div className="absolute inset-0 circuit-bg opacity-40" />
                        <div className="relative h-72 md:h-80 flex items-center">
                            <NetworkGraph />
                        </div>
                        <div className="relative mt-4 flex items-center justify-between font-mono2 text-[11px] tracking-[0.2em] uppercase text-electric">
                            <span>{t.broadband.frameLeft}</span>
                            <span className="animate-energy">{t.broadband.frameRight}</span>
                        </div>
                    </div>
                </Reveal>

                <Reveal className="order-1 lg:order-2">
                    <Eyebrow color="text-electric">{t.broadband.eyebrow}</Eyebrow>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-ink mt-4">
                        {t.broadband.headingA}<span className="text-cyan2">{t.broadband.headingB}</span>
                    </h2>
                    <p className="mt-5 max-w-lg text-ice/60 text-base md:text-lg leading-relaxed">
                        {t.broadband.sub}
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {t.broadband.chips.map((label, i) => (
                            <span
                                key={label}
                                data-testid={`broadband-chip-${i}`}
                                className="flex items-center gap-2 rounded-full border border-electric/30 bg-electric/5 px-4 py-2 text-sm text-ice/85"
                            >
                                <Icon name="wifi" size={15} className="text-cyan2" />
                                {label}
                            </span>
                        ))}
                    </div>
                    <button
                        data-testid="broadband-cta"
                        onClick={() => preselectService("Broadband Connection")}
                        className="mt-10 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-electric to-cyan2 px-8 py-4 font-bold text-white hover:shadow-[0_0_45px_rgba(18,110,255,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
                    >
                        {t.broadband.cta}
                        <Icon name="arrow" size={17} />
                    </button>
                </Reveal>
            </div>
        </section>
    );
}
